import type { NavigationClient } from '@azure/msal-browser'
import { jwtDecode } from 'jwt-decode'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, type ComputedRef, ref, watch } from 'vue'

import { DataServiceFactory } from '@/factories'
import type { ISalesperson } from '@/interfaces'
import { Claims, SecurityGroup, UserInfo } from '@/models'
import {
	CommonService,
	EntraIDAuthConfig,
	type IAuthService,
	type MaybeAccount,
	MicrosoftGraphService
} from '@/services'

export enum AccessLevel {
	None = 0,
	Basic = 1,
	Expanded = 2,
	Full = 3
}

export const useAuthStore = defineStore('auth-store', () => {
	// Constants
	const authService = ref(DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as IAuthService)
	const graphService = ref(
		DataServiceFactory.createService('MicrosoftGraph', authService.value) as MicrosoftGraphService
	)
	const commonService = ref(DataServiceFactory.createService('Common', authService.value) as CommonService)

	const userInfo = ref<UserInfo>(new UserInfo())
	const securityGroups = ref<SecurityGroup[]>([])
	const idToken = ref('')
	const accessToken = ref('')
	const apiAccessToken = ref('')
	const photoBlob = ref('')
	const error = ref('')
	const account = ref<MaybeAccount>(null)
	const initialized = ref(false)
	const roles = ref<string[]>([])
	const salesGroup = ref<number[]>([])
	const allSalesTeams = ref<ISalesperson[]>([])
	const mySalesTeams = ref<ISalesperson[]>([])

	// Computed
	const authenticated = computed<boolean>(() => account.value !== null)

	// Tokens
	const accessTokenObj = computed<Claims>(() => {
		if (accessToken.value === '') {
			return new Claims()
		} else {
			return new Claims(jwtDecode(accessToken.value))
		}
	})
	const idTokenObj = computed<Claims>(() => {
		if (idToken.value === '') {
			return new Claims()
		} else {
			return new Claims(jwtDecode(idToken.value))
		}
	})
	const apiTokenObj = computed<Claims>(() => {
		if (apiAccessToken.value === '') {
			return new Claims()
		} else {
			return new Claims(jwtDecode(apiAccessToken.value))
		}
	})

	watch(
		() => idTokenObj.value,
		async (newToken) => {
			if (newToken) {
				roles.value = newToken.roles
			}
			if (newToken.salesGroup === undefined) {
				salesGroup.value = []
			} else {
				salesGroup.value = newToken.salesGroup?.map((g) => parseInt(g))
			}
		},
		{ deep: true }
	)
	watch(
		[() => salesGroup.value, () => allSalesTeams.value, () => roles.value],
		([newSalesGroup, newAllSalesTeams]) => {
			mySalesTeams.value = newAllSalesTeams.filter(
				(sp: ISalesperson) =>
					newSalesGroup.includes(sp.id) || roles.value.includes('App.Admin') || isSuperUser.value
			)
		},
		{ deep: true }
	)
	const modules = ['ClientIntake', 'Client', 'Event', 'Job', 'SubJob', 'Forecast', 'Quote', 'Estimate']
	const roleLevels = ['Read', 'Edit', 'Add']
	const accessLevels = ['Basic', 'Expanded', 'Full']

	const roleMapping: Record<string, Record<string, string[]>> = modules.reduce(
		(acc, module) => {
			acc[module] = roleLevels.reduce(
				(levelAcc, level) => {
					accessLevels.forEach((accessLevel) => {
						const roleKey = `${level}${accessLevel}`
						levelAcc[roleKey] = [`App.Admin`, `${module}.${level}.${accessLevel}`]
					})
					return levelAcc
				},
				{} as Record<string, string[]>
			)
			return acc
		},
		{} as Record<string, Record<string, string[]>>
	)

	const hasRoleAccess = (module: string, roleType: string) => {
		return computed<boolean>(() => {
			const rolesForType = roleMapping[module][roleType]
			return roles.value.some((role: string) => rolesForType.includes(role))
		})
	}

	const getDynamicAccessLevel = <T extends string>(module: T, roleType: keyof (typeof roleMapping)[T]) => {
		return computed<AccessLevel>(() => {
			if (hasRoleAccess(module, `${roleType}Full`).value) {
				return AccessLevel.Full
			} else if (hasRoleAccess(module, `${roleType}Expanded`).value) {
				return AccessLevel.Expanded
			} else if (hasRoleAccess(module, `${roleType}Basic`).value) {
				return AccessLevel.Basic
			} else {
				return AccessLevel.None
			}
		})
	}

	// const isOpsApprover = computed(() => {
	// 	return roles.value.includes('SCR.Approve.Operations') || roles.value.includes('App.Admin')
	// })

	function isOpsApprover(site: string, salesPerson: number, userPrincipalName: string): boolean {
		const salesTeam = allSalesTeams.value.find((sp) => sp.id === salesPerson)
		if (salesTeam?.email === userPrincipalName) {
			return false
		}
		return (
			roles.value.includes(`SCR.Approve.Operations.${site}`) ||
			roles.value.includes('App.Admin') ||
			userPrincipalName === 'bradh@pinnacle-exp.com'
		)
	}

	const isFinanceApprover = computed(() => {
		return roles.value.includes('SCR.Approve.Finance') || roles.value.includes('App.Admin')
	})

	const isSuperUser = computed(() => {
		// Check if user is admin
		if (roles.value.includes('App.Admin')) {
			return true
		}

		// Check specific email addresses
		const allowedEmails = [
			'jedt@pinnacle-exp.com',
			'chrisj@pinnacle-exp.com',
			'garretts@pinnacle-exp.com',
			'laurent@pinnacle-exp.com'
		]
		if (allowedEmails.includes(userInfo.value.userPrincipalName.toLowerCase())) {
			return true
		}

		// Check job title for 'President' but not 'Vice President'
		const jobTitle = userInfo.value.jobTitle.toLowerCase()
		if (jobTitle.includes('president') && !jobTitle.includes('vice')) {
			return true
		}

		return false
	})

	function getAccessForModuleAndRole<T extends keyof typeof roleMapping>(
		module: T,
		roleType: string
	): ComputedRef<AccessLevel> {
		return getDynamicAccessLevel(module, roleType as keyof (typeof roleMapping)[T])
	}

	function hasAnyDomainAccess<T extends string>(module: T): ComputedRef<boolean> {
		return computed(() => {
			const rolesForModule = roleMapping[module]
			return Object.values(rolesForModule).some((roleArray) =>
				roleArray.some((role) => roles.value.includes(role))
			)
		})
	}

	async function initialize(client?: NavigationClient): Promise<MaybeAccount> {
		// console.debug('Calling AuthStore initialize')
		if (initialized.value === true) {
			return account.value
		}
		return authService.value.initialize(client).then((data) => {
			account.value = data
			initialized.value = true
			commonService.value.getSalespersons().then((data) => {
				allSalesTeams.value = data
			})
			return data
		})
	}

	async function login(): Promise<MaybeAccount> {
		// console.debug('Calling AuthStore login')
		return authService.value
			.login()
			.then(async (data) => {
				account.value = data
				await getAllTokens()
				return data
			})
			.catch((error) => {
				error.value = error
				throw error
			})
	}

	async function logout(): Promise<void> {
		// console.debug('Calling AuthStore logout')
		return authService.value.logout().then(() => {
			account.value = null
		})
	}

	async function getToken(): Promise<void> {
		if (!authService.value.isAuthenticated) {
			throw new Error('Not authenticated')
		}
		// console.debug('Calling AuthStore getToken')
		return authService.value.getToken().then((data) => {
			accessToken.value = data.accessToken || ''
			idToken.value = data.idToken || ''
		})
	}

	async function getGraphData() {
		if (!authService.value.isAuthenticated) {
			throw new Error('Not authenticated')
		}
		// console.debug('Calling AuthStore getGraphData getMe')
		graphService.value.getMe().then((data) => {
			userInfo.value = new UserInfo({
				displayName: data.displayName || '',
				givenName: data.givenName || '',
				id: data.id || 0,
				jobTitle: data.jobTitle || '',
				mail: data.mail || '',
				mobilePhone: data.mobilePhone || '',
				officeLocation: data.officeLocation || '',
				preferredLanguage: data.preferredLanguage || '',
				surname: data.surname || '',
				userPrincipalName: data.userPrincipalName || '',
				photo: ''
			})
		})
		// console.debug('Calling AuthStore getGraphData getGroups')
		graphService.value.getGroups().then((data) => {
			securityGroups.value = data.map(
				(g) =>
					new SecurityGroup({
						displayName: g.displayName || '',
						groupTypes: g.groupTypes,
						id: g.id,
						membershipRule: g.membershipRule || '',
						securityIdentifier: g.securityIdentifier || ''
					})
			)
		})
	}

	async function getPhotoBlob() {
		if (!authService.value.isAuthenticated) {
			throw new Error('Not authenticated')
		}
		// console.debug('Calling AuthStore getPhotoBlob getPhoto')
		graphService.value
			.getPhoto()
			.then((data) => {
				photoBlob.value = data || ''
				userInfo.value = { ...userInfo.value, photo: data }
			})
			.catch((err) => {
				console.error(err)
			})
	}

	async function getAllTokens() {
		// console.debug('Calling AuthStore getAllTokens')

		try {
			await getToken()
		} catch (err) {
			console.error(err)
		}

		try {
			await getGraphData()
			error.value = ''
		} catch (err) {
			console.error(err)
		}

		try {
			await getPhotoBlob()
			error.value = ''
		} catch (err) {
			console.log(err)
		}
	}

	if (!allSalesTeams.value.length) {
		commonService.value.getSalespersons().then((data) => {
			allSalesTeams.value = data
		})
	}

	return {
		graphService,
		authService,
		userInfo,
		securityGroups,
		idToken,
		accessToken,
		apiAccessToken,
		photoBlob,
		error,
		account,
		initialized,
		authenticated,
		roles,
		salesGroup,
		allSalesTeams,
		mySalesTeams,
		accessTokenObj,
		idTokenObj,
		apiTokenObj,
		isOpsApprover,
		isFinanceApprover,
		isSuperUser,

		getAccessForModuleAndRole,
		hasAnyDomainAccess,

		initialize,
		login,
		logout,
		getToken,
		getGraphData,
		getPhotoBlob,
		getAllTokens
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
